import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { optimizedCellClassName } from '../data_table/tableClasses';
import IconButton from '../ui/IconButton';
import Switch from '../ui/Switch';
import Tooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';
import { formatRuleValue } from './optimizerRuleRegistry';

export const constraintTabsSx = {
    minHeight: 34, borderBottom: '1px solid', borderColor: 'divider', mb: 0.75,
    '& .MuiTab-root': { minHeight: 34, px: 1.5, textTransform: 'none', fontSize: '0.78rem' },
};

// The rule tables (portfolio, active, factor and attribute limits) on
// DataTable: they fill their panel and keep their dense cells in both
// densities (5px side padding, 3px above and below so the 36px controls
// clear the row dividers, 12px text), use the 34px rule-table header and let
// long header labels wrap onto a second line. Rows are at least 38px, the kit
// default; rows with controls, two-line labels or hints grow.
export const RULE_TABLE_CLASS_NAME = 'h-full ![--tbl-cell-px:5px] [&_td]:!py-[3px] [&_td]:text-[0.75rem]';
export const RULE_TABLE_HEADER_HEIGHT = 34;

// Each rule-table row holds its cells as nodes under `cells[columnId]`; a row
// with `fullWidth` renders as that one node across the table (DataTable
// renderFullWidthRow).
const renderRuleCell = ({ row, column }) => row.original.cells?.[column.id] ?? null;
export const renderRuleFullWidthRow = (row) => row.fullWidth ?? null;

/**
 * The leaf columns of a rule table, from specs { id, header, width, align,
 * optimized, wrap, meta, ...columnDef }. `width` is the column's share of
 * the table (the shares are scaled to 100%) and the columns' minimum sizes
 * add up to `minWidth`, so a narrower panel scrolls sideways. Each cell shows
 * the row's `cells[id]`. `optimized` tints the header and cells (the OPT
 * column). Cells hold controls and are not cut off unless `wrap` is false.
 * Columns do not sort unless the spec says so (with an accessor and sortFn).
 */
export function ruleColumns(specs, minWidth) {
    const total = specs.reduce((sum, spec) => sum + spec.width, 0);
    return specs.map(({
        id,
        header,
        width,
        align = 'left',
        optimized = false,
        wrap = true,
        meta,
        ...columnDef
    }) => ({
        id,
        header,
        cell: renderRuleCell,
        enableSorting: false,
        minSize: (minWidth * width) / total,
        ...columnDef,
        meta: {
            align,
            wrap,
            width: `${(100 * width) / total}%`,
            cellClassName: optimized ? optimizedCellClassName : undefined,
            headerClassName: optimized ? optimizedCellClassName : undefined,
            ...meta,
        },
    }));
}

// Small secondary text in a rule cell ("Always", "Per name", a scope).
export const ruleNoteClassName = 'text-[0.68rem] text-content-secondary';

export function RuleLabel({ label, unitLabel = null, description = null }) {
    const content = (
        <span className="block min-w-0 leading-normal">
            <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-[0.76rem] font-semibold">
                {label}
            </span>
            {unitLabel && (
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-[0.66rem] text-content-secondary">
                    {unitLabel}
                </span>
            )}
        </span>
    );
    return description
        ? <Tooltip content={description} placement="top-start">{content}</Tooltip>
        : content;
}

RuleLabel.propTypes = {
    label: PropTypes.string.isRequired,
    unitLabel: PropTypes.string,
    description: PropTypes.string,
};

// A baseline or optimized value, in tabular figures; `muted` greys a value
// kept only as context, `tooltip` explains it on hover.
export function RuleValue({ value, unit, tooltip = null, muted = false }) {
    const text = (
        <span className={cx('tabular-nums', muted && 'text-ui-disabled-text')}>
            {formatRuleValue(value, unit)}
        </span>
    );
    return tooltip ? <Tooltip content={tooltip}>{text}</Tooltip> : text;
}

RuleValue.propTypes = {
    value: PropTypes.number,
    unit: PropTypes.oneOf(['usd', 'percent', 'bps', 'count', 'days']).isRequired,
    tooltip: PropTypes.node,
    muted: PropTypes.bool,
};

// A rule that was switched off for the run keeps its values as context only.
export const ruleSwitchedOff = (evaluation) => Boolean(
    evaluation?.configured && !evaluation.applied,
);

/**
 * A rule row's On / Mode control: the search mode editor when the workspace
 * has one, else a switch named "Apply {label} limit" (or `switchLabel`).
 */
export function ruleModeControl(modeEditor, {
    parameter,
    enabled,
    value,
    label,
    disabled,
    onEnabledChange,
    switchLabel,
}) {
    if (modeEditor) return modeEditor({ parameter, enabled, value, label, disabled, onEnabledChange });
    return (
        <Switch
            checked={enabled}
            disabled={disabled}
            onChange={onEnabledChange}
            aria-label={switchLabel ?? `Apply ${label} limit`}
        />
    );
}

// An attached factor or attribute limit: its editor and a remove button.
export function AttachedLimit({ editor, label, onRemove, disabled = false }) {
    const removeLabel = `Remove ${label} limit`;
    return (
        <span className="inline-flex items-center gap-0.5">
            {editor}
            <Tooltip content={removeLabel}>
                <IconButton label={removeLabel} disabled={disabled} onClick={onRemove}>
                    <X aria-hidden="true" size={14} strokeWidth={2.25} />
                </IconButton>
            </Tooltip>
        </span>
    );
}

AttachedLimit.propTypes = {
    editor: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    onRemove: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

// The limit cell of a factor or attribute row without a limit.
export function NotSet() {
    return <span className="text-ui-disabled-text">Not set</span>;
}

export function EmptyScopeMessage({ children }) {
    return (
        <div className="rounded-[10.5px] bg-content-primary/[0.04] p-2.5 dark:bg-content-primary/[0.08]">
            <p className="m-0 text-[0.875rem] leading-[1.43] text-content-secondary [[data-compact]_&]:text-[0.75rem]">
                {children}
            </p>
        </div>
    );
}

EmptyScopeMessage.propTypes = {
    children: PropTypes.node.isRequired,
};
